import React, { useState, useEffect } from 'react';
import { ChevronLeft, ChevronRight, RotateCcw, Save, Check, Layers, Shuffle } from 'react-feather';
import { saveFlashcards } from '../utils/quizStorage';
import styles from '../styles/flashcardDeck.module.css';

/**
 * Props: cards array [{front, back}], title string
 */
const FlashcardDeck = ({ cards, title = 'Flashcards' }) => {
  const [currentCard, setCurrentCard] = useState(0);
  const [isFlipped, setIsFlipped] = useState(false);
  const [isSaved, setIsSaved] = useState(false);
  const [isAnimating, setIsAnimating] = useState(false);
  const [studyMode, setStudyMode] = useState('sequential'); // 'sequential' or 'random'
  const [shuffledCards, setShuffledCards] = useState([]);
  const [studyStats, setStudyStats] = useState({ studied: 0, remaining: 0 });

  useEffect(() => {
    if (studyMode === 'random') {
      const shuffled = [...cards].sort(() => Math.random() - 0.5);
      setShuffledCards(shuffled);
    } else {
      setShuffledCards(cards);
    }
    setCurrentCard(0);
    setIsFlipped(false);
  }, [studyMode, cards]);

  useEffect(() => {
    setStudyStats({
      studied: currentCard,
      remaining: cards.length - currentCard
    });
  }, [currentCard, cards.length]);

  if (!cards || cards.length === 0) {
    return <div className={styles.deckWrapper}>No flashcards available</div>;
  }

  const activeCards = studyMode === 'random' ? shuffledCards : cards;
  const card = activeCards[currentCard];

  const nextCard = () => {
    if (currentCard < activeCards.length - 1) {
      setIsAnimating(true);
      setTimeout(() => {
        setCurrentCard(currentCard + 1);
        setIsFlipped(false);
        setIsAnimating(false);
      }, 150);
    }
  };

  const prevCard = () => {
    if (currentCard > 0) {
      setIsAnimating(true);
      setTimeout(() => {
        setCurrentCard(currentCard - 1);
        setIsFlipped(false);
        setIsAnimating(false);
      }, 150);
    }
  };

  const handleFlip = () => {
    setIsFlipped(!isFlipped);
  };

  const handleSave = () => {
    const flashcardData = {
      title,
      cards,
      id: Date.now().toString(),
      savedAt: new Date().toISOString()
    };
    saveFlashcards(flashcardData);
    setIsSaved(true);
  };

  const toggleStudyMode = () => {
    setStudyMode(prev => prev === 'sequential' ? 'random' : 'sequential');
  };

  const resetDeck = () => {
    setCurrentCard(0);
    setIsFlipped(false);
    if (studyMode === 'random') {
      const shuffled = [...cards].sort(() => Math.random() - 0.5);
      setShuffledCards(shuffled);
    }
  };

  return (
    <div className={styles.deckWrapper}>
      <div className={styles.header}>
        <div className={styles.titleSection}>
          <Layers className={styles.deckIcon} size={20} />
          <h3 className={styles.title}>{title}</h3>
        </div>
        <div className={styles.headerControls}>
          <div className={styles.counter}>
            <span className={styles.current}>{currentCard + 1}</span>
            <span className={styles.divider}>/</span>
            <span className={styles.total}>{activeCards.length}</span>
          </div>
          <div className={styles.progress}>
            <div 
              className={styles.progressFill}
              style={{ width: `${((currentCard + 1) / activeCards.length) * 100}%` }}
            />
          </div>
        </div>
      </div>

      <div className={styles.studyControls}>
        <button 
          className={`${styles.modeBtn} ${studyMode === 'random' ? styles.active : ''}`}
          onClick={toggleStudyMode}
          title={studyMode === 'sequential' ? 'Switch to Random Mode' : 'Switch to Sequential Mode'}
        >
          <Shuffle size={16} />
          {studyMode === 'sequential' ? 'Sequential' : 'Random'}
        </button>
        
        <button 
          className={styles.resetBtn}
          onClick={resetDeck}
          title="Reset Deck"
        >
          <RotateCcw size={16} />
          Reset
        </button>
      </div>

      <div className={styles.cardContainer} onClick={handleFlip}>
        <div className={`${styles.card} ${isFlipped ? styles.flipped : ''} ${isAnimating ? styles.animating : ''}`}>
          <div className={styles.cardFront}>
            <div className={styles.cardLabel}>Front</div>
            <div className={styles.cardContent}>{card.front}</div>
            <div className={styles.flipHint}>Click to flip</div>
          </div>
          <div className={styles.cardBack}>
            <div className={styles.cardLabel}>Back</div>
            <div className={styles.cardContent}>{card.back}</div>
            <div className={styles.flipHint}>Click to flip</div>
          </div>
        </div>
      </div>

      <div className={styles.controls}>
        <button 
          className={styles.navBtn}
          onClick={prevCard}
          disabled={currentCard === 0}
        >
          <ChevronLeft size={18} />
          Previous
        </button>
        
        <div className={styles.centerControls}>
          <button 
            className={`${styles.flipBtn} ${isFlipped ? styles.flipped : ''}`}
            onClick={handleFlip}
          >
            <RotateCcw size={16} />
            {isFlipped ? 'Show Front' : 'Show Back'}
          </button>
          
          <div className={styles.studyStats}>
            <span className={styles.stat}>
              <strong>{studyStats.studied}</strong> studied
            </span>
            <span className={styles.stat}>
              <strong>{studyStats.remaining}</strong> remaining
            </span>
          </div>
        </div>
        
        <button 
          className={styles.navBtn}
          onClick={nextCard}
          disabled={currentCard === activeCards.length - 1}
        >
          Next
          <ChevronRight size={18} />
        </button>
      </div>

      <div className={styles.actions}>
        {!isSaved ? (
          <button className={styles.saveBtn} onClick={handleSave}>
            <Save size={16} />
            Save to Flashcards
          </button>
        ) : (
          <span className={styles.savedLabel}>
            <Check size={16} />
            Saved to Flashcards
          </span>
        )}
      </div>
    </div>
  );
};

export default FlashcardDeck;
